import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";
import { AUCKLAND_KEYWORDS, joinKw } from "@/config/keywords";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/cnhyrmrv_Samoan%20Church%20Uniforms.png";

export default function ChurchUniformsAuckland() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Church Uniforms Auckland",
    serviceType: "Custom church polo uniform design and manufacturing — Auckland",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
    areaServed: { "@type": "City", name: "Auckland" },
  };

  const imageObjects = [{
    url: HERO_IMG,
    alt: "Church Uniforms Auckland — custom Samoan Tongan Cook Islands Pacific Heritage Polo Shirts",
    name: "Church Uniforms Auckland — Pacific Polo Specialist",
    representative: true,
  }];

  return (
    <>
      <SEOHead
        title="Church Uniforms Auckland | Pacific Custom Polo Shirts"
        description="Church Uniforms Auckland — custom Pacific polo shirts for Samoan, Tongan, Cook Islands & Pasifika congregations. South, West & Central Auckland service. Free mockup in 24h."
        path="/church-uniforms-auckland"
        image={HERO_IMG}
        imageAlt="Church Uniforms Auckland — Pacific Heritage Polo Shirts custom design"
        keywords={joinKw(AUCKLAND_KEYWORDS)}
        jsonLd={jsonLd}
        imageObjects={imageObjects}
      />
      <SEOPageLayout
        heroEyebrow="Auckland · Tāmaki Makaurau"
        heroH1="Church Uniforms Auckland — Pacific Polo Specialist"
        heroSub="Custom church polo uniforms designed in Auckland for Samoan, Tongan, Cook Islands and Pacific congregations across South, West and Central Auckland — and all of NZ."
        heroImage={HERO_IMG}
        heroImageAlt="Auckland Pacific church polo uniforms — custom design"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Church Uniforms Auckland" }]}
      >
        <h2>Auckland's specialist for Pacific church uniforms</h2>
        <p>
          Tāmaki Makaurau is home to one of the largest Pacific diasporas
          in the world. Hundreds of Samoan, Tongan, Cook Islands, Fijian,
          Niuean and pan-Pacific congregations across the Auckland region
          gather every Sunday — and every one of them looks better in
          coordinated polo uniforms. <strong>Church uniforms in
          Auckland</strong> from Tribal Designz are made for these
          congregations specifically: by a Pacific-led design studio that
          understands the cultural, denominational and aesthetic nuances of
          each community.
        </p>
        <p>
          We work with churches right across the city — South Auckland,
          West Auckland, Central, North Shore — and ship throughout New
          Zealand for congregations beyond the city.
        </p>

        <h2>Auckland churches we work with</h2>
        <p>
          We've designed uniforms for congregations across most major
          Pacific denominations operating in Auckland, including:
        </p>
        <ul>
          <li>EFKS (Ekalesia Faapotopotoga Kerisiano Samoa)</li>
          <li>CCCS (Congregational Christian Church Samoa)</li>
          <li>PIPC (Pacific Islanders' Presbyterian Church)</li>
          <li>Siasi Uesiliana Tau'atāina (Tongan Free Wesleyan)</li>
          <li>Tongan Methodist Church</li>
          <li>CICC (Cook Islands Christian Church)</li>
          <li>AOG / Assemblies of God Pacific congregations</li>
          <li>Pacific Catholic parishes</li>
          <li>Independent Samoan / Tongan / Cook Islands ministries</li>
        </ul>

        <h2>Suburbs we regularly service</h2>
        <p>
          We outfit church groups across every part of Auckland:
        </p>
        <ul>
          <li>South Auckland — Manukau, Otahuhu, Otara, Papatoetoe, Mangere, Manurewa, Papakura</li>
          <li>West Auckland — Henderson, Te Atatu, Massey, Glen Eden, New Lynn</li>
          <li>Central Auckland — Mt Roskill, Mt Albert, Onehunga, Avondale</li>
          <li>North Shore — Glenfield, Birkenhead, Takapuna</li>
          <li>East Auckland — Botany, Howick, Pakuranga, East Tamaki</li>
        </ul>

        <h2>Auckland advantages</h2>
        <p>
          Because we're based in Auckland, our local church clients get a
          few practical benefits:
        </p>
        <ul>
          <li>In-person design consultation if your committee prefers face-to-face</li>
          <li>Sample / fit kit viewings at our studio</li>
          <li>Pickup option for completed orders (skip the courier fee)</li>
          <li>Faster turnaround on rush jobs — no shipping in transit time</li>
          <li>Direct WhatsApp communication with your designer</li>
        </ul>

        <h2>What we make</h2>
        <p>
          See our <Link to="/samoan-church-uniforms">Samoan church
          uniforms</Link>, <Link to="/tongan-church-uniforms">Tongan church
          uniforms</Link>, <Link to="/cook-islands-uniforms">Cook Islands
          uniforms</Link>, <Link to="/church-conference-uniforms">conference
          uniforms</Link> and <Link to="/reunion-polo-shirts">reunion polo
          shirts</Link> for the full range. The Auckland service area
          applies to all of them.
        </p>

        <h2>Pricing &amp; lead times</h2>
        <p>
          Standard bulk polo pricing — from <strong>$59 per polo</strong>{" "}
          (10–24 units) down to <strong>$49 per polo</strong> at 50+ units,
          custom quotes at 100+. Standard turnaround is 2–3 weeks from
          artwork approval. Local Auckland orders can sometimes be turned
          around faster — talk to us about your deadline.
        </p>

        <h2>Get started</h2>
        <p>
          Send us a quick brief — your church, group, quantity, deadline —
          and we'll come back with a free mockup and itemised quote within
          24 hours.
        </p>
      </SEOPageLayout>
    </>
  );
}
